import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  User as FirebaseUser,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../config/firebase';

export interface RegisterRequest {
  email: string;
  password: string;
  username: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  username: string;
  profile_picture_url?: string;
  public_style_bio?: string;
  created_at: string;
  updated_at: string;
}

export interface LoginResponse {
  user: User;
}

// Convert Firebase user to app User
const firebaseUserToUser = async (firebaseUser: FirebaseUser): Promise<User> => {
  const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
  const userData = userDoc.data();
  
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    username: userData?.username || firebaseUser.displayName || '',
    profile_picture_url: userData?.profile_picture_url || firebaseUser.photoURL || undefined,
    public_style_bio: userData?.public_style_bio || undefined,
    created_at: userData?.created_at?.toDate?.()?.toISOString() || new Date().toISOString(),
    updated_at: userData?.updated_at?.toDate?.()?.toISOString() || new Date().toISOString(),
  };
};

export const authService = {
  register: async (data: RegisterRequest): Promise<LoginResponse> => {
    // Create Firebase auth user
    const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
    
    // Update display name
    await updateProfile(userCredential.user, {
      displayName: data.username,
    });
    
    // Create user document in Firestore
    const userRef = doc(db, 'users', userCredential.user.uid);
    await setDoc(userRef, {
      email: data.email,
      username: data.username,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });
    
    const user = await firebaseUserToUser(userCredential.user);
    return { user };
  },

  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
    const user = await firebaseUserToUser(userCredential.user);
    return { user };
  },

  logout: async () => {
    await signOut(auth);
  },

  getCurrentUser: async (): Promise<User | null> => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        unsubscribe();
        if (firebaseUser) {
          try {
            const user = await firebaseUserToUser(firebaseUser);
            resolve(user);
          } catch (error) {
            console.error('Error getting current user:', error);
            resolve(null);
          }
        } else {
          resolve(null);
        }
      });
    });
  },

  isAuthenticated: (): boolean => {
    return auth.currentUser !== null;
  },

  getStoredUser: (): User | null => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return null;
    
    // Return basic user info from Firebase Auth
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      username: firebaseUser.displayName || '',
      profile_picture_url: firebaseUser.photoURL || undefined,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  },

  uploadProfilePicture: async (file: File): Promise<{ profile_picture_url: string }> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const storageRef = ref(storage, `profile_pictures/${user.uid}/${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    // Update Firebase Auth photo URL
    await updateProfile(user, {
      photoURL: downloadURL,
    });

    // Update Firestore
    const userRef = doc(db, 'users', user.uid);
    await updateDoc(userRef, {
      profile_picture_url: downloadURL,
      updated_at: serverTimestamp(),
    });

    return { profile_picture_url: downloadURL };
  },

  updateUsername: async (username: string): Promise<User> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Update Firebase Auth display name
    await updateProfile(user, {
      displayName: username,
    });

    // Update Firestore
    const userRef = doc(db, 'users', user.uid);
    await updateDoc(userRef, {
      username,
      updated_at: serverTimestamp(),
    });

    return await firebaseUserToUser(user);
  },

  updateEmail: async (email: string): Promise<User> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    await updateEmail(user, email);

    // Update Firestore
    const userRef = doc(db, 'users', user.uid);
    await updateDoc(userRef, {
      email,
      updated_at: serverTimestamp(),
    });

    return await firebaseUserToUser(user);
  },

  updatePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('User not authenticated');

    // Re-authenticate user
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Update password
    await updatePassword(user, newPassword);
  },
};
